import {
  CardData,
  CardPaymentPayload,
  CardPaymentResponse,
  EncryptedCardData,
  FirstCheckoutPaymentResponse,
  ICardHandler,
  OtpAuthPayload,
  PinAuthPayload,
} from '../types';
import axiosInstance from 'utils/axiosInstance';
import { encryptCardDetails, importBase64PublicKey } from 'utils/encryption';
import { extractApiErrorMessage } from '../apiError';

export const FIRSTCHEKOUT_STATUS_PATH = '/firstchekout/payment/status';

// FirstChekout wants the scheme in uppercase: MASTERCARD, VISA, VERVE or
// AFRIGO. The BIN lookup's casing varies, e.g. "mastercard" but "VERVE".
const toCardType = (brand?: string) =>
  brand ? brand.replace(/[^a-z]/gi, '').toUpperCase() : undefined;

export class FirstCheckoutCardHandler implements ICardHandler {
  async encryptCardData(
    cardData: CardData,
    publicKey: string,
  ): Promise<EncryptedCardData> {
    const expMonth = cardData.expirationDate.split('/')[0];
    const expYear = cardData.expirationDate.split('/')[1];

    const key = importBase64PublicKey(publicKey);
    return encryptCardDetails(key, {
      cvv: cardData.cvv,
      number: cardData.cardNumber,
      expiryMonth: expMonth,
      expiryYear: expYear,
      pin: cardData.pin,
    }) as unknown as EncryptedCardData;
  }

  async processPayment(
    _payload: CardPaymentPayload,
  ): Promise<CardPaymentResponse> {
    throw new Error(
      'FirstCheckout only supports /firstchekout/payment through PIN auth processing.',
    );
  }

  async processVisaPayment(
    _payload: CardPaymentPayload,
  ): Promise<CardPaymentResponse> {
    throw new Error('Visa payment processing is not supported by FirstCheckout.');
  }

  /**
   * Initiates the payment. FirstCheckout answers PENDING and sends the
   * cardholder an OTP; the returned transactionReference completes it.
   */
  async processPin(
    payload: PinAuthPayload,
  ): Promise<FirstCheckoutPaymentResponse> {
    if (!payload.cardData || !payload.publicKey || !payload.amount) {
      throw new Error(
        'FirstCheckout PIN processing requires card data, public key, and amount.',
      );
    }

    const card = await this.encryptCardData(
      { ...payload.cardData, pin: payload.pin },
      payload.publicKey,
    );

    const data = await axiosInstance
      .post('/firstchekout/payment', {
        transactionId: payload.ceviantTransactionId,
        amount: payload.amount,
        email: payload.email,
        card,
        cardType: toCardType(payload.cardType),
      })
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(extractApiErrorMessage(error));
      });

    const transactionReference =
      data?.transactionReference ?? data?.data?.transactionReference;

    if (!transactionReference) {
      throw new Error(
        data?.message || 'FirstCheckout did not return a transaction reference.',
      );
    }

    return { ...data, transactionReference };
  }

  async processOtp(payload: OtpAuthPayload) {
    return axiosInstance
      .post('/firstchekout/payment/complete', {
        transactionReference: payload.reference,
        otp: payload.otp,
      })
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(extractApiErrorMessage(error));
      });
  }
}
